import { z } from 'zod';
import { projectBotSchema, type ProjectBot } from '../bot/types';
import { salesRoomHoursSchema, type SalesRoomHours } from './schedule';
import {
  AdvisorFunction,
  CatalogKind,
  ConstructionStatus,
  MediumChannel,
  Role,
  Currency,
  FinancingType,
} from '../enums';
import { botSettingsSchema, companySettingsSchema } from './companySettings';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

// ---------------------------------------------------------------- empresas

export const createCompanySchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: z
    .string()
    .trim()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones'),
  ruc: optionalText(20),
  /** Grupo al que pertenece (por defecto el de la empresa desde la que se crea). */
  groupId: z.string().uuid().optional(),
  settings: companySettingsSchema.partial().optional(),
});
export type CreateCompanyInput = z.infer<typeof createCompanySchema>;

export const updateCompanySchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  ruc: optionalText(20),
  active: z.boolean().optional(),
  /**
   * Se fusiona sobre la configuración actual y se valida completa. El bloque `bot` llega parcial
   * (Empresa manda solo los límites; Bot de atención el resto): sin valores por defecto que pisen
   * lo guardado (encendido, plan, presupuesto, personalidad, plantilla).
   */
  settings: companySettingsSchema
    .partial()
    .extend({ bot: botSettingsSchema.partial().optional() })
    .optional(),
});
export type UpdateCompanyInput = z.infer<typeof updateCompanySchema>;
export type UpdateCompanyPayload = z.input<typeof updateCompanySchema>;

export interface CompanyAdminDto {
  id: string;
  groupId: string;
  groupName: string;
  name: string;
  slug: string;
  ruc: string | null;
  active: boolean;
  settings: z.infer<typeof companySettingsSchema>;
  projects: number;
  users: number;
  createdAt: string;
}

// ---------------------------------------------------------------- proyectos y subdivisiones

// ---------------------------------------------------------------- ficha comercial del proyecto (7.15)

export const PROJECT_PROGRAMS = [
  'MIVIVIENDA',
  'BONO_BUEN_PAGADOR',
  'BONO_VERDE',
  'TECHO_PROPIO',
] as const;
export type ProjectProgram = (typeof PROJECT_PROGRAMS)[number];
export const PROJECT_PROGRAM_LABELS: Record<ProjectProgram, string> = {
  MIVIVIENDA: 'Crédito MiVivienda',
  BONO_BUEN_PAGADOR: 'Bono del Buen Pagador',
  BONO_VERDE: 'Bono Verde (vivienda sostenible)',
  TECHO_PROPIO: 'Techo Propio',
};

/** Ficha comercial: lo que el asesor (y el bot) necesita para cotizar y responder sobre el proyecto. */
export const projectCommercialSchema = z.object({
  /** Monto de separación (reserva) y su moneda. */
  reservationAmount: z.coerce.number().min(0).max(1_000_000_000).optional().nullable(),
  reservationCurrency: z.nativeEnum(Currency).default(Currency.USD),
  /** Porcentaje de cuota inicial sobre el precio. */
  initialFeePercent: z.coerce.number().min(0).max(100).optional().nullable(),
  /** En cuántas cuotas se puede pagar la inicial. */
  initialInstallments: z.coerce.number().int().min(1).max(60).optional().nullable(),
  financingTypes: z.array(z.nativeEnum(FinancingType)).default([]),
  banks: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  programs: z.array(z.enum(PROJECT_PROGRAMS)).default([]),
  /** Promociones vigentes (texto libre). */
  promotions: optionalText(1000),
  /** Lo que el equipo y el bot deben saber (horarios de sala, estacionamientos, reglas…). */
  notes: optionalText(2000),
});
export type ProjectCommercial = z.infer<typeof projectCommercialSchema>;
export type ProjectCommercialPayload = z.input<typeof projectCommercialSchema>;
export const DEFAULT_PROJECT_COMMERCIAL: ProjectCommercial = projectCommercialSchema.parse({});

export const projectInputSchema = z.object({
  name: z.string().trim().min(2).max(120),
  teamId: z.string().uuid().optional().nullable(),
  district: optionalText(80),
  address: optionalText(200),
  latitude: z.coerce.number().min(-90).max(90).optional().nullable(),
  longitude: z.coerce.number().min(-180).max(180).optional().nullable(),
  deliveryDate: z.coerce.date().optional().nullable(),
  constructionStatus: z.nativeEnum(ConstructionStatus).optional().nullable(),
  active: z.boolean().default(true),
  externalIds: z.record(z.unknown()).optional(),
  commercial: projectCommercialSchema.optional(),
  /** Lo que sabe el bot de este proyecto (ADR-020). */
  bot: projectBotSchema.optional(),
  /** Horario de la sala de ventas; null = el de la empresa. */
  salesRoomHours: salesRoomHoursSchema.nullable().optional(),
});
export const createProjectSchema = projectInputSchema.extend({ companyId: z.string().uuid() });
export const updateProjectSchema = projectInputSchema.partial();
export type CreateProjectInput = z.infer<typeof createProjectSchema>;
export type CreateProjectPayload = z.input<typeof createProjectSchema>;
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

export const subdivisionInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  code: z.string().trim().min(1).max(20),
  sortOrder: z.coerce.number().int().min(0).default(0),
});
export type SubdivisionInput = z.infer<typeof subdivisionInputSchema>;

export interface ProjectAdminDto {
  id: string;
  companyId: string;
  companyName: string;
  teamId: string | null;
  name: string;
  active: boolean;
  district: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  deliveryDate: string | null;
  constructionStatus: ConstructionStatus | null;
  externalIds: Record<string, unknown>;
  commercial: ProjectCommercial;
  bot: ProjectBot;
  salesRoomHours: SalesRoomHours | null;
  subdivisions: Array<{ id: string; name: string; code: string; sortOrder: number; units: number }>;
  units: number;
  leads: number;
  createdAt: string;
}

// ---------------------------------------------------------------- usuarios y membresías

export const membershipInputSchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  role: z.nativeEnum(Role),
});
export type MembershipInput = z.infer<typeof membershipInputSchema>;

const passwordSchema = z
  .string()
  .min(8, 'La contraseña debe tener al menos 8 caracteres')
  .max(200)
  .regex(/[A-Za-z]/, 'Debe incluir letras')
  .regex(/\d/, 'Debe incluir números');

export const createUserSchema = z.object({
  username: z
    .string()
    .trim()
    .min(3)
    .max(40)
    .regex(/^[a-z0-9._-]+$/, 'Solo minúsculas, números, punto, guion y guion bajo'),
  email: z.string().trim().email('Correo inválido').max(120),
  password: passwordSchema,
  firstName: z.string().trim().min(2).max(80),
  lastName: z.string().trim().min(1).max(80),
  phone: optionalText(30),
  advisorFunction: z.nativeEnum(AdvisorFunction).optional().nullable(),
  isTestAccount: z.boolean().default(false),
  memberships: z.array(membershipInputSchema).min(1, 'Asigna al menos una empresa'),
  externalIds: z.record(z.unknown()).optional(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type CreateUserPayload = z.input<typeof createUserSchema>;

export const updateUserSchema = z.object({
  email: z.string().trim().email().max(120).optional(),
  firstName: z.string().trim().min(2).max(80).optional(),
  lastName: z.string().trim().min(1).max(80).optional(),
  phone: optionalText(30),
  advisorFunction: z.nativeEnum(AdvisorFunction).optional().nullable(),
  isTestAccount: z.boolean().optional(),
  active: z.boolean().optional(),
  /**
   * Reemplaza las membresías del usuario solo en las empresas que vienen en la lista; las de
   * otras empresas (incluidas las ocultas) se conservan tal cual.
   */
  memberships: z.array(membershipInputSchema).optional(),
  /** Empresas de las que se retira al usuario por completo (quien edita debe administrarlas). */
  removeCompanyIds: z.array(z.string().uuid()).max(50).optional(),
  externalIds: z.record(z.unknown()).optional(),
  /**
   * Lote 6 · I2: a quién pasan los leads activos (y sus tareas pendientes) que el usuario dejaría de poder atender
   * al desactivarlo o al perder el rol de ventas (o el acceso a un proyecto) en una empresa: por empresa, un asesor
   * asignable o `POOL` (la bolsa). Sin esto, y con leads afectados, la API responde 400 `HANDOVER_REQUIRED`.
   */
  handover: z
    .record(z.string().uuid(), z.object({ to: z.union([z.literal('POOL'), z.string().uuid()]) }))
    .optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type UpdateUserPayload = z.input<typeof updateUserSchema>;

/** Lote 6 · I2: lo que un usuario deja a su nombre en una empresa (vista previa de la baja o del cambio de rol). */
export interface HandoverCompanyDto {
  companyId: string;
  companyName: string;
  /** Leads ACTIVO a su nombre que dejaría de poder atender. */
  leads: number;
  /** Tareas pendientes (no borradas ni simuladas) de esos leads. */
  tasks: number;
  /** Reglas de reparto donde figura (en el turno o como coordinador destino). */
  rules: number;
  /** Asesores que pueden recibir sus leads en la empresa (sin cuentas de prueba ni él mismo). */
  candidates: Array<{ id: string; fullName: string; role: Role }>;
}
export interface HandoverPreviewDto {
  companies: HandoverCompanyDto[];
}
/** Resumen de lo que pasó al guardar con traspaso (por empresa afectada). */
export interface HandoverResultDto {
  companies: Array<{
    companyId: string;
    companyName: string;
    /** Leads movidos en esta llamada (los cerrados no se tocan). */
    leads: number;
    /** Tareas pendientes movidas con ellos. */
    tasks: number;
    /** Reglas de reparto de las que salió. */
    rules: number;
    /** Reglas por turnos que quedaron sin asesores (siguen activas; los leads nuevos van a la bolsa hasta elegir asesores). */
    emptyRules: number;
    to: 'POOL' | string;
    toName: string | null;
  }>;
}

export const resetPasswordSchema = z.object({ password: passwordSchema });

export const userFiltersSchema = z.object({
  companyId: z.string().uuid().optional(),
  q: z.string().trim().max(80).optional(),
  active: z.coerce.boolean().optional(),
  kind: z.enum(['HUMAN', 'BOT']).optional(),
});

export interface UserAdminDto {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone: string | null;
  kind: 'HUMAN' | 'BOT';
  advisorFunction: AdvisorFunction | null;
  active: boolean;
  isTestAccount: boolean;
  lastLoginAt: string | null;
  /** Solo las membresías en empresas que administra quien consulta (las demás no se muestran). */
  memberships: Array<{
    id: string;
    companyId: string;
    companyName: string;
    projectId: string | null;
    projectName: string | null;
    role: Role;
  }>;
  /** Si quien consulta puede editarlo, cambiarle la clave, desactivarlo o cambiar sus membresías. */
  canManage: boolean;
  /** Motivo cuando `canManage` es false (el mismo mensaje que devolvería la API). */
  manageBlockedReason: string | null;
  /** Empresas donde es el único administrador activo: no se le puede quitar ADMIN ni desactivar. */
  soleAdminOf: Array<{ companyId: string; companyName: string }>;
  createdAt: string;
  /** Solo en la respuesta de `PATCH /admin/users/:id` cuando hubo traspaso de leads (Lote 6 · I2). */
  handover?: HandoverResultDto;
}

// ---------------------------------------------------------------- catálogos

export const catalogItemInputSchema = z.object({
  companyId: z.string().uuid(),
  kind: z.nativeEnum(CatalogKind),
  code: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[A-Z0-9_]+$/, 'Solo mayúsculas, números y guion bajo'),
  label: z.string().trim().min(1).max(120),
  group: optionalText(40),
  sortOrder: z.coerce.number().int().min(0).default(0),
  metadata: z.record(z.unknown()).optional(),
  active: z.boolean().default(true),
});
export type CatalogItemInput = z.infer<typeof catalogItemInputSchema>;
export type CatalogItemPayload = z.input<typeof catalogItemInputSchema>;

export const mediumCategoryInputSchema = z.object({
  companyId: z.string().uuid(),
  code: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[A-Z0-9_]+$/, 'Solo mayúsculas, números y guion bajo'),
  name: z.string().trim().min(1).max(120),
  channel: z.nativeEnum(MediumChannel),
  isDigital: z.boolean().default(false),
  sortOrder: z.coerce.number().int().min(0).default(0),
  active: z.boolean().default(true),
});
export type MediumCategoryInput = z.infer<typeof mediumCategoryInputSchema>;
export type MediumCategoryPayload = z.input<typeof mediumCategoryInputSchema>;

export const mediumInputSchema = z.object({
  companyId: z.string().uuid(),
  name: z.string().trim().min(1).max(80),
  categoryCode: z.string().trim().min(1).max(40),
  active: z.boolean().default(true),
});
export type MediumInput = z.infer<typeof mediumInputSchema>;
export type MediumPayload = z.input<typeof mediumInputSchema>;

export interface CatalogAdminResponse {
  companyId: string;
  items: Array<{
    id: string;
    kind: CatalogKind;
    code: string;
    label: string;
    group: string | null;
    sortOrder: number;
    metadata: Record<string, unknown>;
    active: boolean;
  }>;
  mediumCategories: Array<{
    id: string;
    code: string;
    name: string;
    channel: MediumChannel;
    isDigital: boolean;
    sortOrder: number;
    active: boolean;
    media: number;
  }>;
  media: Array<{ id: string; name: string; categoryCode: string; active: boolean }>;
}
